// Lógica pura do tempo real de `faturas` no Financeiro (Janela de Cobrança e
// tabela principal). Fica fora do React para ser testável — ver
// __tests__/realtimeFaturas.test.ts. Mesmo desenho de views/CRM/Kanban/realtimeNegociacoes.ts.

type FaturaMinima = { id: number | string; status_fatura?: string | null; uc_codigo?: string | null };

/**
 * O que uma ação da Janela de Cobrança mexeu, para a tela recarregar só isso.
 * Sem alvo, quem recebe faz a carga cheia.
 */
export interface AlvoAtualizacao {
  /** Faturas gravadas pela ação (update ou insert). */
  faturaIds?: number[];
  /** UCs cujas faturas podem ter aparecido por fora (upload no n8n, robô). */
  ucCodigos?: string[];
  /** Cadastro da UC alterado (titular, histórico manual). */
  ucIds?: number[];
}

/** Id da fatura num evento de postgres_changes. DELETE só traz a PK em `old`. */
export const idDoEventoFatura = (payload: { new?: any; old?: any }): number | null => {
  const bruto = payload?.new?.id ?? payload?.old?.id;
  const id = Number(bruto);
  return bruto != null && Number.isFinite(id) ? id : null;
};

/**
 * Aplica as faturas recarregadas por id sobre a lista da tela.
 *
 * Id pedido que não voltou (apagado, ou o RLS não deixa mais ver) ou que voltou
 * "Excluído" sai da lista — a carga cheia também não traz excluídas. Id novo entra
 * no fim (a ordem de exibição é decidida por quem renderiza). Sem mudança, devolve
 * a mesma referência para não recalcular os useMemo da tela à toa.
 */
export const aplicarPatchFaturas = <T extends FaturaMinima>(lista: T[], encontradas: T[], idsPedidos: number[]): T[] => {
  if (!idsPedidos.length) return lista;
  const porId = new Map<number, T>();
  for (const f of encontradas) {
    if (f.status_fatura !== 'Excluído') porId.set(Number(f.id), f);
  }
  const pedidos = new Set(idsPedidos.map(Number));

  const saida: T[] = [];
  const jaNaLista = new Set<number>();
  let mudou = false;
  for (const atual of lista) {
    const id = Number(atual.id);
    if (!pedidos.has(id)) {
      saida.push(atual);
      continue;
    }
    jaNaLista.add(id);
    const nova = porId.get(id);
    mudou = true;
    if (nova) saida.push(nova);
  }
  for (const id of pedidos) {
    if (jaNaLista.has(id)) continue;
    const nova = porId.get(id);
    if (nova) {
      saida.push(nova);
      mudou = true;
    }
  }
  return mudou ? saida : lista;
};

/**
 * Ids a recarregar quando o alvo é uma UC e não uma fatura (upload manual, robô):
 * as faturas que a tela já tem dessa UC — podem ter mudado ou sumido — mais as
 * que o banco devolveu agora, que podem ser novas.
 */
export const idsDasFaturasDaUc = (
  lista: FaturaMinima[],
  encontradas: FaturaMinima[],
  ucCodigos: string[],
): number[] => {
  const ucs = new Set(ucCodigos.map((u) => String(u).replace(/\D/g, '').replace(/^0+/, '')).filter(Boolean));
  const ids = new Set<number>();
  for (const f of lista) {
    const uc = String(f.uc_codigo ?? '').replace(/\D/g, '').replace(/^0+/, '');
    if (uc && ucs.has(uc)) ids.add(Number(f.id));
  }
  for (const f of encontradas) ids.add(Number(f.id));
  return Array.from(ids);
};

/** Divide em blocos: `in.(...)` com milhares de ids estoura o tamanho da URL (HTTP 400). */
export const emBlocos = <T,>(itens: T[], tamanho: number): T[][] => {
  const blocos: T[][] = [];
  for (let i = 0; i < itens.length; i += tamanho) blocos.push(itens.slice(i, i + tamanho));
  return blocos;
};
